import React, {useEffect} from 'react';
import MaterialTable, { MTableActions } from "material-table";
import { useTranslation } from 'react-i18next';
import { Button, CircularProgress, IconButton, MuiThemeProvider, Tooltip } from '@material-ui/core';
import {SystemUpdateAlt, DeleteForever, GetApp, Close} from '@material-ui/icons';
import { useStores } from '../../_stores';
import { autorun } from 'mobx';
import { Observer, observer, useLocalStore } from 'mobx-react-lite';
import { IFullComponent } from '../../_types/interfaces';
import { Notification } from '../../core/Notification';
import { Description } from '@material-ui/icons';
import PatchNotes from '../PatchNotes/PatchNotes';
import theme from '../../core/TableTheme';
import { LicenseImport } from '../../core/LicenseImport';
import { MainStyles } from './Styles/MainStyles';
import { FilterType } from '../../_types/enums/FilterType';

const Indiv = observer(() => {
    const classes = MainStyles();
    const {updateStore, componentStore, patchNotesStore, importStore} = useStores();
    
    const materialTableRef = React.createRef<any>();

    const state = useLocalStore(() => ({
        isOpen: false,
        isImportLicense: false,
        isSelection: false,
        isDo: "",
    }));

    const {t} = useTranslation();
    const translate = (variable: string) => {
        return t('updates.' + variable);
    };

    

    const OnRowSelect = (props: any) => {
        const update = async () => {
            if (materialTableRef.current) //убрать выделение
                materialTableRef.current.onAllSelected(false);
            if(componentStore.filter == FilterType.Update)
                await updateStore.downloadFiles((props.data as Array<IFullComponent>).map(d => d.name));
            if(componentStore.filter == FilterType.Install)
                await componentStore.addComponents((props.data as Array<IFullComponent>).map(m => m.name));
            if(componentStore.filter == FilterType.Delete)
                await componentStore.deleteComponents((props.data as Array<IFullComponent>).map(m => m.name));
            
            state.isSelection = false;
            componentStore.filter = FilterType.None;
            }

        const updateCheck = () => {
            if (materialTableRef.current)
                materialTableRef.current.onAllSelected(false);
        }

        const setDo = (name: string) => state.isDo = name;

        return (
            <div className={classes.actions}>
                <Button
                    className={classes.update}
                    size={"large"}
                    onClick={async () => await update()}
                >
                    {state.isDo}
                </Button>
                <Tooltip title="Сбросить все фильтры">
                    <Button 
                        startIcon={<Close/>} 
                        className={classes.update}
                        size={"large"}
                        onClick={() => {
                            updateCheck();
                            state.isSelection = false;
                            componentStore.filter = FilterType.None;
                        }}
                    >
                    </Button>
                </Tooltip>
                <Tooltip title="Выбрать компоненты для установки">
                    <Button 
                        startIcon={<GetApp/>} 
                        className={classes.update}
                        size={"large"}
                        onClick={() => {
                            updateCheck(); 
                            setDo("Установить");
                            componentStore.filter = FilterType.Install;
                        }}
                    >
                    </Button>
                </Tooltip>
                <Tooltip title="Выбрать компоненты для удаления">
                    <Button 
                        startIcon={<DeleteForever/>} 
                        className={classes.update}
                        size={"large"}
                        onClick={() => {
                            updateCheck(); 
                            setDo("Удалить");
                            componentStore.filter = FilterType.Delete;
                        }}
                    >
                    </Button>
                </Tooltip>
                <Tooltip title="Выбрать компоненты для обновления">
                    <Button 
                        startIcon={<SystemUpdateAlt/>} 
                        className={classes.update}
                        size={"large"}
                        onClick={() => {
                            updateCheck(); 
                            setDo("Обновить");
                            componentStore.filter = FilterType.Update;
                        }}
                    >
                    </Button>
                </Tooltip>
            </div>
        );
    };

    useEffect(() => {
        autorun(async () => {
            await updateStore.loadServices();
            await componentStore.getAddress();
            await componentStore.loadServices();
            await componentStore.loadPlugins();
            await componentStore.loadFullComponents(updateStore.services);
        });
    }, []);

    return (
        <div>
            {
                componentStore.loading &&
                <div className={classes.loading}> <CircularProgress disableShrink/> </div>
            }
            <h2 className = {classes.title}>{"Главная страница"}</h2>
            
            <MuiThemeProvider theme={theme}>
                <Observer>{() => (
                    <MaterialTable
                        localization={t('tables', {returnObjects: true})}
                        title={''}
                        columns={[
                            {field: 'name', title: translate("columns.name"), cellStyle: {paddingRight: "100px"}},
                            {field: 'currentVersion', title: translate("columns.currentVersion"), cellStyle: { width: '1%'}},
                            {field: 'currentFile', title: t('patchNotes.currentPatchNote'), 
                                render: rowData => (
                                <IconButton 
                                    style = {{marginLeft: '20px'}} 
                                    onClick = { async () => {
                                        if(!rowData.name.startsWith("Plugin"))
                                            await patchNotesStore.loadLocalPatchNotesService(rowData.name);
                                        else
                                            await patchNotesStore.loadLocalPatchNotesPlugin(rowData.name);
                                        state.isOpen = true; 
                                        }}>
                                    <Description/>
                                </IconButton>)
                            },
                            {field: 'newVersion', title: translate('columns.newVersion'), cellStyle: { width: '1%', }},
                            {field: 'newFile', title: t('patchNotes.newPatchNote'), 
                                render: rowData => (
                                <IconButton  
                                    style = {{marginLeft: '20px'}} 
                                    onClick = { async () => {
                                        await patchNotesStore.loadGlobalPatchNote(rowData.name);
                                        state.isOpen = true;                     
                                        }}>
                                    <Description/>
                                </IconButton>)
                            },
                        ]}
                        data={componentStore.getFullComponents}
                        actions={[
                            {
                                icon: 'update',
                                tooltip: 'Проверка обновлений',
                                isFreeAction: true,
                                onClick: async () => updateStore.sendRequestDownloadService(),
                            },
                            {
                                icon: 'system_update_alt',
                                tooltip: 'Выбрать компоненты для обновления',
                                isFreeAction: true,
                                onClick: () => { 
                                    state.isSelection = true;
                                    state.isDo = "Обновить";
                                    componentStore.filter = FilterType.Update;
                                },
                            },
                            {
                                icon: 'delete_forever',
                                tooltip: 'Выбрать компоненты для удаления',
                                isFreeAction: true,
                                onClick: () => { 
                                    state.isSelection = true;
                                    state.isDo = "Удалить";
                                    componentStore.filter = FilterType.Delete;
                                },
                            },
                            {
                                icon: 'get_app',
                                tooltip: 'Выбрать компоненты для установки',
                                isFreeAction: true,
                                onClick: () => {
                                    state.isSelection = true;
                                    state.isDo = "Установить";
                                    componentStore.filter = FilterType.Install;
                                },
                            },
                            {
                                icon: 'close',
                                tooltip: 'Сбросить все фильтры',
                                isFreeAction: true,
                                hidden: !state.isSelection,
                                onClick: () => {
                                    componentStore.filter = FilterType.None;
                                    state.isSelection = false;
                                },
                            },
                        ]}
                        options={{
                            search: false,
                            selection: state.isSelection,
                            paging: false,
                        }}
                        components={{
                            Actions: props => {
                                // Отрисовка, если есть данные
                                if (props.hasOwnProperty('data'))
                                    return <OnRowSelect {...props}/>
                                return <MTableActions {...props} /> 
                            },
                        }}
                        tableRef={materialTableRef}
                    />)}
                </Observer>
                </MuiThemeProvider>
                
            <Notification
                isOpen={componentStore.isNotification}
                close={() => componentStore.isNotification = false}
                okClick={() => componentStore.isNotification = false}
                questionText={componentStore.message}
            />
            <PatchNotes
                isOpen={state.isOpen}
                close={() => state.isOpen = false}
            />
        </div>
    )
});

export default Indiv;

